import { refreshOAuth2Token } from '@/services/oauth2';
import { createAppRequest } from '@/services/request';
import type {
  AddIssueResolutionInput,
  AssignPlantIssueInput,
  AttachmentReference,
  CarePlan,
  CarePlanInput,
  CareTask,
  CareTaskQuery,
  ChangeRecord,
  ClosePlantIssueInput,
  CompleteCareTaskInput,
  CreateCareTaskInput,
  CreateChangeRecordInput,
  CreatePlantIssueInput,
  CreatePlantPlacementInput,
  DataScopeGrant,
  DataScopeGrantInput,
  DeferCareTaskInput,
  GreenplantAutomationStatus,
  GreenplantInsights,
  GreenplantOverview,
  PageQuery,
  PageResult,
  PlantIssue,
  PlantIssueDetail,
  PlantIssueQuery,
  PlantPlacement,
  PlantPlacementDetail,
  PlantPlacementQuery,
  SpacePoint,
  SpacePointInput,
  SpacePointQuery,
} from './types';

const displayAttachmentName = (value: string) => {
  if (Array.from(value).some((character) => character.charCodeAt(0) > 255)) {
    return value;
  }

  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(value, (character) => character.charCodeAt(0)),
    );
  } catch {
    return value;
  }
};

const request = createAppRequest();
const API_PREFIX = '/bff/greenplant';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
};

type PagePayload<T> =
  | PageResult<T>
  | T[]
  | {
      list?: T[];
      items?: T[];
      data?: T[];
      total?: number;
      page?: number;
      pageSize?: number;
      limit?: number;
      totalPages?: number;
    };

const unwrap = <T>(response: T | ApiEnvelope<T>): T => {
  if (
    response &&
    typeof response === 'object' &&
    'success' in response &&
    (response as ApiEnvelope<T>).success === false
  ) {
    throw new Error('Greenplant API returned an unsuccessful response');
  }

  if (
    response &&
    typeof response === 'object' &&
    'data' in response &&
    (response as ApiEnvelope<T>).data !== undefined
  ) {
    return (response as ApiEnvelope<T>).data as T;
  }

  return response as T;
};

const normalizePage = <T>(payload: PagePayload<T>): PageResult<T> => {
  if (Array.isArray(payload)) {
    return { list: payload, total: payload.length };
  }

  const pagePayload = payload as {
    list?: T[];
    items?: T[];
    data?: T[];
    total?: number;
    page?: number;
    pageSize?: number;
    limit?: number;
  };
  const list = pagePayload.list ?? pagePayload.items ?? pagePayload.data ?? [];
  return {
    list,
    total: pagePayload.total ?? list.length,
    page: pagePayload.page,
    pageSize: pagePayload.pageSize ?? pagePayload.limit,
  };
};

const getPage = async <T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<PageResult<T>> => {
  const { pageSize, ...restParams } = params ?? {};
  const boundedPageSize =
    typeof pageSize === 'number'
      ? Math.min(Math.max(1, pageSize), 100)
      : undefined;
  const response = await request<PagePayload<T> | ApiEnvelope<PagePayload<T>>>(
    `${API_PREFIX}${path}`,
    {
      method: 'GET',
      params: {
        ...restParams,
        ...(boundedPageSize === undefined ? {} : { limit: boundedPageSize }),
      },
    },
  );
  if (
    response &&
    typeof response === 'object' &&
    'success' in response &&
    (response as ApiEnvelope<PagePayload<T>>).success === false
  ) {
    throw new Error('Greenplant API returned an unsuccessful response');
  }

  const responseData =
    response && typeof response === 'object' && 'data' in response
      ? (response as ApiEnvelope<PagePayload<T>>).data
      : undefined;
  const payload =
    responseData &&
    typeof responseData === 'object' &&
    !Array.isArray(responseData)
      ? responseData
      : (response as PagePayload<T>);
  return normalizePage(payload);
};

const getResource = async <T>(
  path: string,
  params?: Record<string, unknown>,
) => {
  const response = await request<T | ApiEnvelope<T>>(`${API_PREFIX}${path}`, {
    method: 'GET',
    params,
  });
  return unwrap(response);
};

const mutateResource = async <T, TPayload = unknown>(
  path: string,
  method: 'POST' | 'PATCH' | 'PUT',
  data?: TPayload,
) => {
  const response = await request<T | ApiEnvelope<T>>(`${API_PREFIX}${path}`, {
    method,
    data,
  });
  return unwrap(response);
};

export const getOverview = () => getResource<GreenplantOverview>('/overview');

export const getPlantPlacements = (query: PlantPlacementQuery) =>
  getPage<PlantPlacement>('/placements', query as Record<string, unknown>);

export const getPlantPlacement = (id: string) =>
  getResource<PlantPlacementDetail>(`/placements/${encodeURIComponent(id)}`);

export const createPlantPlacement = (payload: CreatePlantPlacementInput) =>
  mutateResource<PlantPlacement, CreatePlantPlacementInput>(
    '/placements',
    'POST',
    payload,
  );

export const updatePlantPlacement = (
  id: string,
  payload: Partial<CreatePlantPlacementInput>,
) =>
  mutateResource<PlantPlacement, Partial<CreatePlantPlacementInput>>(
    `/placements/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );

export const removePlantPlacement = async (id: string) => {
  const response = await request<
    { id: string; deleted: true } | ApiEnvelope<{ id: string; deleted: true }>
  >(`${API_PREFIX}/placements/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return unwrap(response);
};

export const getSpacePoints = (query: SpacePointQuery) =>
  getPage<SpacePoint>('/space-points', query as Record<string, unknown>);

export const createSpacePoint = (payload: SpacePointInput) =>
  mutateResource<SpacePoint, SpacePointInput>('/space-points', 'POST', payload);

export const updateSpacePoint = (
  id: string,
  payload: Partial<Omit<SpacePointInput, 'code'>>,
) =>
  mutateResource<SpacePoint, Partial<Omit<SpacePointInput, 'code'>>>(
    `/space-points/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );

export const removeSpacePoint = async (id: string) => {
  const response = await request<SpacePoint | ApiEnvelope<SpacePoint>>(
    `${API_PREFIX}/space-points/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  return unwrap(response);
};

export const createChangeRecord = (payload: CreateChangeRecordInput) =>
  mutateResource<ChangeRecord, CreateChangeRecordInput>(
    '/change-records',
    'POST',
    payload,
  );

export const getCareTasks = (query: CareTaskQuery) =>
  getPage<CareTask>('/care-tasks', query as Record<string, unknown>);

export const getCareTask = (id: string) =>
  getResource<CareTask>(`/care-tasks/${encodeURIComponent(id)}`);

export const createCareTask = (payload: CreateCareTaskInput) =>
  mutateResource<CareTask, CreateCareTaskInput>('/care-tasks', 'POST', payload);

export const startCareTask = (id: string) =>
  mutateResource<CareTask>(
    `/care-tasks/${encodeURIComponent(id)}/start`,
    'POST',
  );

export const completeCareTask = (id: string, payload: CompleteCareTaskInput) =>
  mutateResource<CareTask, CompleteCareTaskInput>(
    `/care-tasks/${encodeURIComponent(id)}/complete`,
    'POST',
    payload,
  );

export const deferCareTask = (id: string, payload: DeferCareTaskInput) =>
  mutateResource<CareTask, DeferCareTaskInput>(
    `/care-tasks/${encodeURIComponent(id)}/defer`,
    'POST',
    payload,
  );

export const getPlantIssues = (query: PlantIssueQuery) =>
  getPage<PlantIssue>('/issues', query as Record<string, unknown>);

export const getPlantIssue = (id: string) =>
  getResource<PlantIssueDetail>(`/issues/${encodeURIComponent(id)}`);

export const createPlantIssue = (payload: CreatePlantIssueInput) =>
  mutateResource<PlantIssue, CreatePlantIssueInput>('/issues', 'POST', payload);

export const assignPlantIssue = (id: string, payload: AssignPlantIssueInput) =>
  mutateResource<PlantIssue, AssignPlantIssueInput>(
    `/issues/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );

export const addIssueResolution = (
  id: string,
  payload: AddIssueResolutionInput,
) =>
  mutateResource<PlantIssueDetail, AddIssueResolutionInput>(
    `/issues/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );

export const closePlantIssue = (id: string, payload: ClosePlantIssueInput) =>
  mutateResource<PlantIssueDetail, ClosePlantIssueInput>(
    `/issues/${encodeURIComponent(id)}/close`,
    'POST',
    payload,
  );

export const reopenPlantIssue = (id: string, reason: string) =>
  mutateResource<PlantIssueDetail, { reason: string; status: 'processing' }>(
    `/issues/${encodeURIComponent(id)}`,
    'PATCH',
    { reason, status: 'processing' },
  );

export const getInsights = (params: { period?: string; areaId?: string }) =>
  getResource<GreenplantInsights>('/insights', params);

export const getGreenplantAutomationStatus = () =>
  getResource<GreenplantAutomationStatus>('/automation-status');

export const getCarePlans = (query: PageQuery) =>
  getPage<CarePlan>('/care-plans', query as Record<string, unknown>);

export const createCarePlan = (payload: CarePlanInput) =>
  mutateResource<CarePlan, CarePlanInput>('/care-plans', 'POST', payload);

export const updateCarePlan = (id: string, payload: CarePlanInput) =>
  mutateResource<CarePlan, CarePlanInput>(
    `/care-plans/${encodeURIComponent(id)}`,
    'PATCH',
    payload,
  );

export const getDataScopeGrants = (query: PageQuery = {}) =>
  getPage<DataScopeGrant>(
    '/management/identity/data-scopes',
    query as Record<string, unknown>,
  );

export const upsertDataScopeGrant = (payload: DataScopeGrantInput) =>
  mutateResource<DataScopeGrant, DataScopeGrantInput>(
    '/management/identity/data-scopes',
    'PUT',
    payload,
  );

export const revokeDataScopeGrant = async (id: string) => {
  const response = await request<DataScopeGrant | ApiEnvelope<DataScopeGrant>>(
    `${API_PREFIX}/management/identity/data-scopes/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
  return unwrap(response);
};

export const bootstrapAdministratorDataScope = () =>
  mutateResource<DataScopeGrant[], undefined>(
    '/management/identity/data-scope/bootstrap',
    'POST',
  );

const isUnauthorized = (error: unknown) => {
  if (typeof error !== 'object' || error === null) return false;

  const requestError = error as {
    response?: { status?: number };
    status?: number;
    statusCode?: number;
    message?: string;
  };
  if (
    requestError.response?.status === 401 ||
    requestError.status === 401 ||
    requestError.statusCode === 401
  ) {
    return true;
  }

  // Umi can turn an HTTP failure into BizError before this adapter receives it.
  // The platform uses this exact message when it has invalidated a user token.
  return (
    requestError.message?.includes('Token 对应凭证不存在或已停用') ?? false
  );
};

const sendAttachmentUpload = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const response = await request<
    AttachmentReference | ApiEnvelope<AttachmentReference>
  >(`${API_PREFIX}/attachments`, {
    method: 'POST',
    data: formData,
    skipErrorHandler: true,
  });
  return unwrap(response);
};

export const uploadAttachment = async (file: File) => {
  try {
    return await sendAttachmentUpload(file);
  } catch (error) {
    if (!isUnauthorized(error) || !(await refreshOAuth2Token())) {
      throw error;
    }
    return sendAttachmentUpload(file);
  }
};

export const getAttachmentBlob = async (
  attachment: Pick<AttachmentReference, 'id' | 'name'>,
  preview = false,
) => {
  return request<Blob>(
    `${API_PREFIX}/attachments/${encodeURIComponent(attachment.id)}`,
    {
      method: 'GET',
      responseType: 'blob',
      params: preview ? { preview: 'true' } : undefined,
      // A missing cover image should fall back to the card placeholder without
      // surfacing a global error toast while the asset page loads.
      skipErrorHandler: preview,
    },
  );
};

export const downloadAttachment = async (
  attachment: Pick<AttachmentReference, 'id' | 'name'>,
) => {
  const blob = await getAttachmentBlob(attachment);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = displayAttachmentName(attachment.name);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};
